import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import {
  EmailCompose,
  type ComposeContact,
  type ComposeDraft,
  type ComposeFile,
} from '../email/EmailCompose.js'

export interface SendAddressee {
  /** Job contacts that have an email, in the order they sit on the job. */
  recipients: ComposeContact[]
  /**
   * Who to preselect: the billing contact's address, or the client's own
   * when nobody on the job is marked. Decision 84.
   */
  fallbackTo: string | null
  jobId?: string
  files?: ComposeFile[]
}

/**
 * Emailing a quote or an invoice.
 *
 * The same compose window as every other email. The send is still the
 * document procedure: it renders the PDF, records the version, and on a
 * quote mints the accept link. The PDF is the locked attachment.
 *
 * An invoice raised on a client's parent, or c/- it, starts from where its
 * rule sends it: the parent's accounts, the owner, or both with the owner
 * copied in, and offers each by name (decision 123). Every other bill starts
 * from the job's billing contact. A second address in To goes as a copy
 * rather than being dropped, because a document is sent to one person.
 */
export function SendDocumentPopup({
  kind,
  id,
  number,
  addressee,
  open,
  onClose,
  subject,
  onSent,
}: {
  kind: 'quote' | 'invoice'
  id: string
  number: string
  addressee: SendAddressee
  open: boolean
  onClose: () => void
  /** In place of "Quote QTE-…" — a reminder says it is one. */
  subject?: string
  onSent?: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const sendQuote = useMutation(trpc.quotes.send.mutationOptions())
  const sendInvoice = useMutation(trpc.invoices.send.mutationOptions())
  const send = kind === 'quote' ? sendQuote : sendInvoice
  const label = kind === 'quote' ? 'quote' : 'invoice'
  const billed = useQuery({
    ...trpc.invoices.recipients.queryOptions({ id }),
    enabled: open && kind === 'invoice',
  })
  const rule = billed.data ?? null
  const contacts = rule
    ? [
        ...rule.options.map((option) => ({ name: option.label, email: option.email })),
        ...addressee.recipients.filter(
          (person) => !rule.options.some((option) => option.email.toLowerCase() === person.email.toLowerCase()),
        ),
      ]
    : addressee.recipients
  const to = rule?.to ? [rule.to.email] : addressee.fallbackTo ? [addressee.fallbackTo] : []

  const submit = async (draft: ComposeDraft) => {
    await send.mutateAsync({
      id,
      to: draft.to[0] ?? null,
      message: draft.body === '' ? null : draft.body,
      ...(draft.subject ? { subject: draft.subject } : {}),
      ...(draft.to.length + draft.cc.length > 1 ? { cc: [...draft.to.slice(1), ...draft.cc] } : {}),
      ...(draft.bcc.length > 0 ? { bcc: draft.bcc } : {}),
      ...(draft.attachmentIds.length > 0 ? { attachmentIds: draft.attachmentIds } : {}),
    })
    void queryClient.invalidateQueries(trpc.quotes.pathFilter())
    void queryClient.invalidateQueries(trpc.invoices.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    void queryClient.invalidateQueries(trpc.pipeline.pathFilter())
    onSent?.()
  }

  return (
    <EmailCompose
      // Opens once the rule's recipients are known, which it starts from.
      open={open && !billed.isLoading}
      onClose={onClose}
      title={kind === 'quote' ? 'Send quote' : 'Email invoice'}
      sendLabel={kind === 'quote' ? 'Send quote' : 'Send invoice'}
      contacts={contacts}
      files={addressee.files ?? []}
      jobId={addressee.jobId}
      document={{ kind, id }}
      allowBlankTo
      subjectOptional
      bodyOptional
      initial={{
        to,
        cc: rule?.cc.map((copy) => copy.email) ?? [],
        subject: subject ?? `${label[0]!.toUpperCase()}${label.slice(1)} ${number}`,
      }}
      locked={[{ name: `${number}.pdf`, detail: 'Attached when you send' }]}
      onSend={async (draft) => {
        try {
          await submit(draft)
        } catch (error) {
          throw new Error(errorMessage(error))
        }
      }}
    />
  )
}
